import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_CARDS } from 'graphql/queries';
import {
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_FOR_TENANT,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';

// The composed (cache-only) read of the heavy visitors document is refreshed by the two slim queries below,
// so the heavy document itself must not be refetched.
export const refetchQueries = [
  { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS }
];

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitorGroupsForTenant'>> =
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_VISITORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> = GET_ALL_VISITORS_FOR_TENANT;
const GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> =
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_VISITORS_CARDS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> = GET_ALL_VISITORS_CARDS;

const useAllVisitors = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: visitorsLoading,
    data: visitorsData,
    error: visitorsError
  } = useQuery(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (visitorsError) handleFetchError(translation.users_error_fetching);
  }, [visitorsError]);

  const {
    loading: groupsLoading,
    data: groupsData,
    error: groupsError
  } = useQuery(GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (groupsError) handleFetchError(translation.user_groups_error_fetching);
  }, [groupsError]);

  const visitorsPending = (visitorsLoading && !visitorsData) || (groupsLoading && !groupsData);

  // Keep the combined view in a refetching state until all background requests have finished.
  // Some queries may already be refreshed while others are still in progress.
  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingAllVisitors =
    !visitorsPending && ((!!visitorsData && visitorsLoading) || (!!groupsData && groupsLoading));

  useRegisterBackgroundRefetch(isRefetchingAllVisitors);

  const {
    loading: visitorsComposedLoading,
    data: allVisitorsData,
    error: visitorsComposedError
  } = useQuery(GET_ALL_VISITORS_FOR_TENANT_TYPED, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: visitorsPending
  });

  useEffect(() => {
    if (visitorsComposedError) handleFetchError(translation.users_error_fetching);
  }, [visitorsComposedError]);

  const handleCardsError = (error: Error) => {
    handleFetchError(translation.nfc_cards_error_fetching);
    console.error(error);
  };

  const {
    loading: cardsLoading,
    data: cardsData,
    error: cardsError
  } = useQuery(GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (cardsError) handleCardsError(cardsError);
  }, [cardsError]);

  const cardsPending = cardsLoading && !cardsData;

  const isRefetchingCards = !!cardsData && cardsLoading;

  useRegisterBackgroundRefetch(isRefetchingCards);

  const {
    loading: cardsComposedLoading,
    data: allVisitorsCardsData,
    error: cardsComposedError
  } = useQuery(GET_ALL_VISITORS_CARDS_TYPED, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: cardsPending
  });

  useEffect(() => {
    if (cardsComposedError) handleCardsError(cardsComposedError);
  }, [cardsComposedError]);

  // Apollo 4 types results with `returnPartialData: true` as DeepPartial,
  // because some nested fields may be missing from the cache.
  // We cast them to the GraphQL types to preserve the existing hook API.
  // Consumers already handle the potentially missing nested fields.
  return {
    allVisitors: (allVisitorsData?.getVisitors as Query['getVisitors'] | undefined) || [],
    loadingAllVisitors: visitorsPending || visitorsComposedLoading,
    isRefetchingAllVisitors,
    allVisitorsCards: (allVisitorsCardsData?.getNfcCards as Query['getNfcCards'] | undefined) || [],
    loadingAllVisitorsCards: cardsPending || cardsComposedLoading,
    isRefetchingCards
  };
};

export default useAllVisitors;
